import { describeApiError, isCommandOutcome } from '@/api/problemDetails';
import { markRaw, ref, type App, type Component, type InjectionKey, type Plugin, type Raw } from 'vue';

//the drawer is as wide as one of three presets: md carries a single column of fields, lg a form
//whose fields pair up two to a row, xl a file to read beside the fields. The classes are spelled
//out because Tailwind only sees literal class names
export type DrawerSize = 'md' | 'lg' | 'xl';

const drawerSizeClasses: Record<DrawerSize, string> = {
    md: 'w-full! max-w-md!',
    lg: 'w-full! max-w-lg!',
    xl: 'w-full! max-w-7xl!'
};

export interface DrawerActionProps {
    label?: string;
    severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger';
    outlined?: boolean;
    text?: boolean;
    icon?: string;
}

//what an accept may hand back when its command succeeded only in part, such as a disconnection whose
//PlugChoice delete failed: the drawer stays open with the warning, so the user can read it and retry
export interface DrawerOutcome {
    warning?: string;
}

type AcceptResult = void | DrawerOutcome | Promise<void | DrawerOutcome>;

export interface DrawerOptions<T = void> {
    title: string;
    message?: string;
    icon?: string;
    component?: Raw<Component>;
    props?: Record<string, unknown>;
    size?: DrawerSize;
    acceptProps?: DrawerActionProps;
    rejectProps?: DrawerActionProps;
    //the caller owns the API call: accept runs the command and the refetch, and the drawer only
    //closes once it has resolved
    accept?: T extends void ? () => AcceptResult : (data: T) => AcceptResult;
    reject?: () => void;
}

export interface DrawerService {
    require<T = unknown>(options: DrawerOptions<T>): void;
    confirm<T = unknown>(data?: T): Promise<void>;
    cancel(): void;
}

export const DrawerServiceKey: InjectionKey<DrawerService> = Symbol('DrawerService');

export const drawerState = {
    visible: ref(false),
    title: ref(''),
    message: ref<string | undefined>(undefined),
    icon: ref<string | undefined>(undefined),
    component: ref<Raw<Component> | null>(null),
    props: ref<Record<string, unknown>>({}),
    sizeClass: ref(drawerSizeClasses.md),
    acceptProps: ref<DrawerActionProps>({ label: 'Confirm' }),
    rejectProps: ref<DrawerActionProps>({ label: 'Cancel', severity: 'secondary', outlined: true }),
    //accept is running; the buttons wait for it
    busy: ref(false),
    //the outcome of the last accept when the command was refused (400, 404, 409): shown at the
    //bottom of the drawer, which stays open with the input intact
    error: ref<string | undefined>(undefined),
    //the warning an accept returned after its command succeeded in part: shown at the bottom of the
    //drawer, which stays open
    warning: ref<string | undefined>(undefined),
    onAccept: null as ((data: unknown) => AcceptResult) | null,
    onReject: null as (() => void) | null
};

function close() {
    drawerState.visible.value = false;
    drawerState.error.value = undefined;
    drawerState.warning.value = undefined;
    drawerState.onAccept = null;
    drawerState.onReject = null;
}

// Exported as DrawerService — matches PrimeVue's ConfirmationService naming convention
const DrawerService: Plugin = {
    install(app: App) {
        const service: DrawerService = {
            require<T>(options: DrawerOptions<T>) {
                drawerState.title.value = options.title;
                drawerState.message.value = options.message;
                drawerState.icon.value = options.icon;
                drawerState.component.value = options.component ? markRaw(options.component) : null;
                drawerState.props.value = options.props ?? {};
                drawerState.sizeClass.value = drawerSizeClasses[options.size ?? 'md'];
                drawerState.acceptProps.value = { label: 'Confirm', ...options.acceptProps };
                drawerState.rejectProps.value = { label: 'Cancel', severity: 'secondary', outlined: true, ...options.rejectProps };
                drawerState.busy.value = false;
                drawerState.error.value = undefined;
                drawerState.warning.value = undefined;
                drawerState.onAccept = (options.accept as (data: unknown) => AcceptResult) ?? null;
                drawerState.onReject = options.reject ?? null;
                drawerState.visible.value = true;
            },

            //runs accept and closes on success. A success that returns a warning stays in the drawer
            //as that warning; a refused command stays as its error; any other failure closes the
            //drawer and is rethrown for the host to toast
            async confirm<T>(data?: T) {
                const onAccept = drawerState.onAccept;
                drawerState.busy.value = true;
                drawerState.error.value = undefined;
                drawerState.warning.value = undefined;
                try {
                    const outcome = await onAccept?.(data);
                    if (outcome?.warning) {
                        drawerState.warning.value = outcome.warning;
                        return;
                    }
                    close();
                } catch (error) {
                    const description = describeApiError(error);
                    if (isCommandOutcome(description.status)) {
                        drawerState.error.value = description.message;
                        return;
                    }
                    close();
                    throw error;
                } finally {
                    drawerState.busy.value = false;
                }
            },

            cancel() {
                const onReject = drawerState.onReject;
                close();
                onReject?.();
            }
        };

        app.provide(DrawerServiceKey, service);
        app.config.globalProperties.$drawer = service;
    }
};

export default DrawerService;
